import { z } from "zod";

import {
  supportRequestFieldsSchema,
  supportRequestTypeSchema,
} from "./support-request";

const sourceMessageId = z.string().min(1).max(128).regex(/\S/u);
const orderReference = z
  .string()
  .trim()
  .pipe(
    supportRequestFieldsSchema.shape.order_reference
      .min(1)
      .regex(/^#?[A-Za-z0-9][A-Za-z0-9-]*$/),
  );
const orderContext = z.strictObject({
  type: z.literal("support_request_context"),
  version: z.literal(1),
  source_message_id: sourceMessageId,
  order_reference: orderReference.nullable(),
});
const requestContext = z.strictObject({
  type: z.literal("support_request_context"),
  version: z.literal(2),
  source_message_id: sourceMessageId,
  // 内容分别兜底；合法类型不依赖订单，非法类型也不丢失合法订单引用。
  request_type: supportRequestTypeSchema.catch("other_contact_support"),
  order_reference: orderReference.nullable().optional().catch(undefined),
});

/** 转人工正文中的本轮预填信息；不包含订单访问资格或顾客最终选择。 */
export const supportRequestContextEnvelopeSchema = z.discriminatedUnion(
  "version",
  [orderContext, requestContext],
);

export type SupportRequestContextEnvelope = z.infer<
  typeof supportRequestContextEnvelopeSchema
>;

const blockEnvelope = z.looseObject({ type: z.string() });
const transferBlock = blockEnvelope.extend({
  type: z.literal("transfer"),
  text: z.array(z.looseObject({ value: z.string() })).min(1),
});
const contextEnvelope = z.object({
  type: z.literal("support_request_context"),
});

/** 只提取唯一、合法的版本化 transfer context，不从顾客可见文本推断。 */
export function readSupportRequestContext(
  content: string,
): SupportRequestContextEnvelope | null {
  let raw: unknown;
  try {
    raw = JSON.parse(content) as unknown;
  } catch {
    return null;
  }
  const blocks = z.array(blockEnvelope).safeParse(raw);
  if (!blocks.success) return null;
  const contexts: unknown[] = [];
  for (const block of blocks.data) {
    if (block.type !== "transfer") continue;
    const transfer = transferBlock.safeParse(block);
    if (!transfer.success) return null;
    for (const item of transfer.data.text) {
      let value: unknown;
      try {
        value = JSON.parse(item.value) as unknown;
      } catch {
        continue;
      }
      if (contextEnvelope.safeParse(value).success) contexts.push(value);
    }
  }
  if (contexts.length !== 1) return null;
  const parsed = supportRequestContextEnvelopeSchema.safeParse(contexts[0]);
  return parsed.success ? parsed.data : null;
}
